import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import { NEVER, Observable, ReplaySubject, Subject } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { Identifiable } from '../../entities/common/identifiable.interface';
import { RecordSecurityGroups } from '../../entities/management/record-security-groups.entity';
import { SecurityGroup } from '../../entities/management/security-group.entity';
import { LoadingBehaviorSubject } from '../../helpers/loading-behavior-subject.helper';
import { waitForIndex } from '../../helpers/wait-for-index.helper';
import { ToastHelper } from '../../toast/service/toast.helper';
import { RecordSecurityGroupsSandboxService } from '../sandbox/record-security-groups-sandbox.service';

@Component({
    standalone: false,
    selector: 'digihaven-record-security-groups-container',
    templateUrl: './record-security-groups-container.component.html',
    styleUrls: ['./record-security-groups-container.component.scss']
})
export class RecordSecurityGroupsContainerComponent implements OnInit {
    readonly securityGroupsLoading$: LoadingBehaviorSubject = new LoadingBehaviorSubject();

    recordSecurityGroups$!: Observable<SecurityGroup[]>;
    readonlySecurityGroups$!: Observable<SecurityGroup[]>;
    editableSeriesSecurityGroups$!: Observable<SecurityGroup[]>;

    record$: Subject<Identifiable> = new ReplaySubject<Identifiable>(1);

    @Input()
    set record(record: Identifiable) {
        this.record$.next(record);
    }

    @Output() errored: EventEmitter<Error> = new EventEmitter();

    constructor(
        private readonly sandbox: RecordSecurityGroupsSandboxService,
        protected translateService: TranslateService,
        protected activatedRoute: ActivatedRoute,
        protected router: Router,
        protected toastHelper: ToastHelper
    ) {}

    ngOnInit(): void {
        this.initSourceStreams();
    }

    private initSourceStreams(): void {
        this.recordSecurityGroups$ = this.getSeriesSecurityGroups();
        this.readonlySecurityGroups$ = this.getSeriesReadonlySecurityGroups();
        this.editableSeriesSecurityGroups$ = this.getSeriesEditableSecurityGroups();
    }

    private getSeriesSecurityGroups(): Observable<SecurityGroup[]> {
        this.securityGroupsLoading$.startLoading();

        return this.record$.pipe(
            switchMap((record: Identifiable) => this.sandbox.getSecurityGroupsOfRecord(record)),
            waitForIndex(),
            tap(() => this.securityGroupsLoading$.stopLoading()),
            catchError(this.handleSecurityGroupError.bind(this)),
            map((seriesSecurityGroups: RecordSecurityGroups) => seriesSecurityGroups.SecurityGroups),
            shareReplay()
        );
    }

    private getSeriesReadonlySecurityGroups(): Observable<SecurityGroup[]> {
        return this.recordSecurityGroups$.pipe(
            map((seriesSecurityGroups: SecurityGroup[]) =>
                seriesSecurityGroups.filter((securityGroup: SecurityGroup) => securityGroup.IsReadOnly)
            )
        );
    }

    private getSeriesEditableSecurityGroups(): Observable<SecurityGroup[]> {
        return this.recordSecurityGroups$.pipe(
            map((seriesSecurityGroups: SecurityGroup[]) =>
                seriesSecurityGroups.filter((securityGroup: SecurityGroup) => !securityGroup.IsReadOnly)
            )
        );
    }

    private handleSecurityGroupError(err: Error): Observable<never> {
        this.errored.emit(err);
        return NEVER;
    }
}
